const QTY_OPTION_NAMES = new Set(['quantity', 'الكمية', 'كمية']);
const HIDDEN_NATIVE = 'quantity-tier-stepper__native';
const HIDDEN_FALLBACK = 'quantity-tier-fallback--hidden';

function normalizeName(value) {
  return String(value || '').trim().toLocaleLowerCase();
}

function tierNumber(value) {
  const arabic = '٠١٢٣٤٥٦٧٨٩';
  const eastern = '۰۱۲۳۴۵۶۷۸۹';
  const text = String(value || '')
    .replace(/[٠-٩]/g, (digit) => arabic.indexOf(digit))
    .replace(/[۰-۹]/g, (digit) => eastern.indexOf(digit))
    .replace(/[\s,٬،]/g, '');
  const numbers = text.match(/\d+/g);
  if (!numbers || numbers.length !== 1) return null;
  const number = Number(numbers[0]);
  return Number.isSafeInteger(number) && number >= 1000 && number % 1000 === 0 ? number : null;
}

function readTierOption(container) {
  const radios = [...container.querySelectorAll('input[type="radio"][data-option-name]')];
  let mode = 'radio';
  let select;
  let elements = radios;

  if (radios.length < 2) {
    select = container.querySelector('select');
    if (!(select instanceof HTMLSelectElement) || select.options.length < 2) return null;
    mode = 'select';
    elements = [...select.options];
  }

  const optionName = elements[0]?.dataset.optionName;
  if (!QTY_OPTION_NAMES.has(normalizeName(optionName))) return null;

  const items = elements.map((element) => ({
    element,
    value: element.value,
    number: tierNumber(element.value),
    available:
      !element.disabled &&
      element.dataset.optionAvailable !== 'false' &&
      element.getAttribute('aria-disabled') !== 'true',
  }));

  if (
    items.some((item) => item.number === null) ||
    elements.some((element) => normalizeName(element.dataset.optionName) !== normalizeName(optionName)) ||
    new Set(items.map((item) => item.number)).size !== items.length
  ) {
    return null;
  }

  items.sort((a, b) => a.number - b.number);
  return { mode, select, items };
}

function selectedIndex(tier) {
  const index = tier.items.findIndex(({ element }) =>
    element instanceof HTMLInputElement ? element.checked : element.selected
  );
  return index < 0 ? 0 : index;
}

function nextIndex(tier, current, direction) {
  for (let index = current + direction; index >= 0 && index < tier.items.length; index += direction) {
    if (tier.items[index].available) return index;
  }
  return -1;
}

function selectTier(tier, index) {
  const item = tier.items[index];
  if (!item) return;
  if (tier.mode === 'radio') {
    if (item.element instanceof HTMLInputElement && !item.element.checked) item.element.click();
  } else if (tier.select instanceof HTMLSelectElement && tier.select.value !== item.value) {
    tier.select.value = item.value;
    tier.select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function productScope(container) {
  const picker = container.closest('variant-picker');
  return picker?.closest('quick-add-dialog, dialog, product-information, featured-product-information, .shopify-section') || document;
}

function hideFallback(container, hidden) {
  for (const fallback of productScope(container).querySelectorAll('.quantity-tier-fallback')) {
    fallback.classList.toggle(HIDDEN_FALLBACK, hidden);
  }
}

function restoreNative(container) {
  container.querySelector(':scope > .quantity-tier-stepper')?.remove();
  container.removeAttribute('data-quantity-tier-enhanced');
  for (const element of container.querySelectorAll(`.${HIDDEN_NATIVE}`)) {
    element.classList.remove(HIDDEN_NATIVE);
    element.removeAttribute('aria-hidden');
  }
}

function hideNative(tier) {
  if (tier.mode === 'radio') {
    for (const { element } of tier.items) {
      if (!(element instanceof HTMLInputElement)) continue;
      for (const label of element.labels || []) {
        label.classList.add(HIDDEN_NATIVE);
        label.setAttribute('aria-hidden', 'true');
      }
    }
  } else {
    const wrapper = tier.select?.closest('.variant-option__select-wrapper');
    wrapper?.classList.add(HIDDEN_NATIVE);
    wrapper?.setAttribute('aria-hidden', 'true');
  }
}

function signature(tier) {
  return tier.items
    .map(({ element, value, number, available }) => {
      const selected = element instanceof HTMLInputElement ? element.checked : element.selected;
      return `${value}:${number}:${available ? 1 : 0}:${selected ? 1 : 0}`;
    })
    .join('|');
}

function enhance(container) {
  if (container.closest('product-card, swatches-variant-picker-component')) return;

  const hasQuantityName = [...container.querySelectorAll('[data-option-name]')].some((element) =>
    QTY_OPTION_NAMES.has(normalizeName(element.dataset.optionName))
  );
  if (!hasQuantityName) return;

  const tier = readTierOption(container);
  if (!tier) {
    restoreNative(container);
    hideFallback(container, false);
    return;
  }

  const tierSignature = signature(tier);
  const existing = container.querySelector(':scope > .quantity-tier-stepper');
  if (existing?.dataset.signature === tierSignature) {
    hideFallback(container, true);
    return;
  }

  restoreNative(container);
  hideNative(tier);
  hideFallback(container, true);
  container.dataset.quantityTierEnhanced = 'true';

  const arabic = document.documentElement.lang.toLowerCase().startsWith('ar');
  const stepper = document.createElement('div');
  stepper.className = 'quantity-tier-stepper';
  stepper.dataset.signature = tierSignature;

  const minus = document.createElement('button');
  minus.type = 'button';
  minus.className = 'quantity-tier-stepper__button button button-unstyled';
  minus.setAttribute('aria-label', arabic ? 'الكمية السابقة' : 'Previous quantity');
  minus.innerHTML = '<span aria-hidden="true">−</span>';

  const value = document.createElement('output');
  value.className = 'quantity-tier-stepper__value';
  value.tabIndex = 0;
  value.setAttribute('role', 'spinbutton');
  value.setAttribute('aria-label', arabic ? 'الكمية المختارة' : 'Selected quantity');
  value.setAttribute('aria-live', 'polite');
  value.setAttribute('aria-valuemin', tier.items[0].number);
  value.setAttribute('aria-valuemax', tier.items[tier.items.length - 1].number);

  const plus = document.createElement('button');
  plus.type = 'button';
  plus.className = 'quantity-tier-stepper__button button button-unstyled';
  plus.setAttribute('aria-label', arabic ? 'الكمية التالية' : 'Next quantity');
  plus.innerHTML = '<span aria-hidden="true">+</span>';

  function render(index) {
    const item = tier.items[index];
    value.value = String(item.number);
    value.textContent = String(item.number);
    value.setAttribute('aria-valuenow', item.number);
    value.setAttribute('aria-valuetext', item.value);
    minus.disabled = nextIndex(tier, index, -1) < 0;
    plus.disabled = nextIndex(tier, index, 1) < 0;
  }

  function move(direction) {
    const index = nextIndex(tier, selectedIndex(tier), direction);
    if (index < 0) return;
    render(index);
    selectTier(tier, index);
  }

  minus.addEventListener('click', () => move(-1));
  plus.addEventListener('click', () => move(1));
  value.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(1);
    }
  });

  render(selectedIndex(tier));
  stepper.append(minus, value, plus);
  container.append(stepper);
}

function enhanceAll() {
  for (const container of document.querySelectorAll('variant-picker .variant-option')) enhance(container);
}

let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    enhanceAll();
  });
}

function installStyles() {
  if (document.getElementById('quantity-tier-stepper-styles')) return;
  const style = document.createElement('style');
  style.id = 'quantity-tier-stepper-styles';
  style.textContent = `
    .${HIDDEN_NATIVE}, .${HIDDEN_FALLBACK} { display: none !important; }
    .quantity-tier-stepper {
      display: grid;
      grid-template-columns: var(--minimum-touch-target, 44px) minmax(7.5rem, 1fr) var(--minimum-touch-target, 44px);
      align-items: stretch;
      inline-size: min(100%, 22rem);
      min-block-size: var(--minimum-touch-target, 44px);
      margin-block-start: var(--margin-2xs, .5rem);
      border: var(--style-border-width-inputs, 1px) solid var(--color-variant-border, currentcolor);
      border-radius: var(--style-border-radius-inputs, 0);
      background: var(--color-variant-background, var(--color-background));
      color: var(--color-variant-text, var(--color-foreground));
      direction: ltr;
      overflow: hidden;
    }
    .quantity-tier-stepper__button {
      display: grid;
      place-items: center;
      min-inline-size: var(--minimum-touch-target, 44px);
      min-block-size: var(--minimum-touch-target, 44px);
      padding: 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: inherit;
      font-size: 1.35rem;
      line-height: 1;
    }
    .quantity-tier-stepper__button:first-child { border-inline-end: 1px solid var(--color-variant-border, currentcolor); }
    .quantity-tier-stepper__button:last-child { border-inline-start: 1px solid var(--color-variant-border, currentcolor); }
    .quantity-tier-stepper__button:not(:disabled):hover { background: var(--color-variant-hover-background, transparent); }
    .quantity-tier-stepper__button:disabled { opacity: .35; cursor: not-allowed; }
    .quantity-tier-stepper__value {
      display: grid;
      place-items: center;
      min-inline-size: 0;
      padding-inline: var(--padding-sm, .75rem);
      color: inherit;
      font: inherit;
      font-weight: var(--font-weight-semibold, 600);
      text-align: center;
      unicode-bidi: isolate;
    }
    .quantity-tier-stepper__value:focus-visible {
      outline: var(--focus-outline-width, 2px) solid currentcolor;
      outline-offset: calc(-1 * var(--focus-outline-width, 2px));
    }
  `;
  document.head.append(style);
}

function initialize() {
  installStyles();
  enhanceAll();
  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('variant-picker')) schedule();
  });
  document.addEventListener('shopify:section:load', schedule);
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initialize, { once: true })
  : initialize();
